import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { useMe } from '../session.js';
import { formatPrice, useLocale } from './site.js';
import {
  Button,
  Checkbox,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SelectField,
  TextField,
} from './ui.js';

export interface ServiceRequestTarget {
  readonly siteId?: string | undefined;
  readonly findingId?: string | undefined;
  readonly incidentId?: string | undefined;
  readonly serviceCode?: string | undefined;
}

const PHONE = /^\+?[0-9 ()/-]{6,30}$/;
/** The API's limit on everyone answers "Retry in N s"; its daily limit of requests does not. */
const waitAMoment = (error: unknown) =>
  error instanceof ApiError &&
  error.code === 'RATE_LIMITED' &&
  /^Retry in/.test(error.problem.detail ?? '');

/**
 * US-05, T-90 (site-help.html): the service, the terms, a message, how to reach the client and the
 * consent; then what was sent, or the daily limit. No price: the client receives a quote at the
 * hourly rate, the plan's discount already in it (decision 3 of SP-UX10.5).
 */
export function ServiceRequestForm({
  target,
  backTo,
}: {
  target: ServiceRequestTarget;
  /** Where "Abbrechen" leads: the problem or the site. */
  backTo: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const client = useQueryClient();
  const messageId = useId();
  const catalogue = useQuery({
    queryKey: ['services', locale],
    queryFn: () => unwrap(api.GET('/services', { params: { query: { locale } } })),
    staleTime: 60_000,
  });
  const [code, setCode] = useState(target.serviceCode ?? '');
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState<'email' | 'phone'>('email');
  const [phone, setPhone] = useState('');
  // The account's name until the client types another one (sheet: the field is filled in).
  const [name, setName] = useState<string | null>(null);
  const contactName = name ?? me.data?.user.fullName ?? '';
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<{ phone?: string; consent?: string }>({});
  const send = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/service-requests', {
          body: {
            ...(target.siteId ? { siteId: target.siteId } : {}),
            ...(target.findingId ? { findingId: target.findingId } : {}),
            ...(target.incidentId ? { incidentId: target.incidentId } : {}),
            ...(code ? { serviceCode: code } : {}),
            ...(message.trim() ? { message: message.trim() } : {}),
            ...(contactName.trim() ? { contactName: contactName.trim() } : {}),
            // The number is always on the form: kept when given, needed for a call back.
            ...(phone.trim() ? { contactPhone: phone.trim() } : {}),
            preferredContact: contact,
            contactConsent: true,
          },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['requests'] }),
  });
  // The button is gone once sent: the focus moves to what happened, so a screen reader says it.
  const doneTitle = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (send.isSuccess) doneTitle.current?.focus();
  }, [send.isSuccess]);

  if (catalogue.isPending) return <Loading />;
  if (catalogue.isError)
    return (
      <LoadError
        message={errorMessage(catalogue.error, t)}
        onRetry={() => void catalogue.refetch()}
      />
    );
  const myRequests = (
    <LinkButton to="/requests" variant="secondary" size="sm">
      {t('request.toList')}
    </LinkButton>
  );
  if (
    send.error instanceof ApiError &&
    send.error.code === 'RATE_LIMITED' &&
    !waitAMoment(send.error)
  )
    return (
      <div className="help-limit">
        <Notice kind="warning" title={t('request.limitTitle')} action={myRequests}>
          {t('request.limitText')}
        </Notice>
      </div>
    );
  if (send.isSuccess) {
    const r = send.data;
    // Named in the language of the page: the API names it in the account's language.
    const serviceName =
      catalogue.data.items.find((s) => s.code === r.serviceCode)?.name ?? r.serviceName;
    return (
      <div className="card help">
        <div className="done">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M7 12.5l3.2 3.2L17 9" />
          </svg>
          <h2 className="h3s" ref={doneTitle} tabIndex={-1}>
            {t('request.sent')}
          </h2>
          <p className="bodyM">{t('request.sentText')}</p>
        </div>
        <dl className="kv">
          <div>
            <dt>{t('request.service')}</dt>
            <dd>{serviceName ?? t('request.general')}</dd>
          </div>
          {r.siteName ? (
            <div>
              <dt>{t('request.site')}</dt>
              <dd>{r.siteName}</dd>
            </div>
          ) : null}
          <div>
            <dt>{t('request.contactShort')}</dt>
            <dd>
              {r.preferredContact === 'phone'
                ? t('request.byPhone', { value: r.contactPhone ?? '' })
                : t('request.byEmail', { value: me.data?.user.email ?? '' })}
            </dd>
          </div>
        </dl>
        <div className="acts">
          <LinkButton to="/requests" variant="primary">
            {t('request.toList')}
          </LinkButton>
          {target.siteId ? (
            <LinkButton to={`/sites/${target.siteId}`} variant="secondary">
              {t('request.toSite')}
            </LinkButton>
          ) : null}
        </div>
      </div>
    );
  }

  const { items: services, hourlyRateCents, discountPct } = catalogue.data;
  const selected = services.find((s) => s.code === code);
  const plan = me.data?.account.planCode;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if ((contact === 'phone' || phone.trim()) && !PHONE.test(phone.trim()))
      next.phone = t('request.phoneInvalid');
    if (!consent) next.consent = t('request.consentRequired');
    setErrors(next);
    if (Object.keys(next).length === 0) send.mutate();
  };

  return (
    <form onSubmit={submit} noValidate className="card help">
      {send.error ? <Notice kind="error">{errorMessage(send.error, t)}</Notice> : null}
      <SelectField
        label={t('request.service')}
        hint={selected?.description}
        value={code}
        onChange={(e) => setCode(e.target.value)}
      >
        <option value="">{t('request.general')}</option>
        {services.map((s) => (
          <option key={s.code} value={s.code}>
            {s.name}
          </option>
        ))}
      </SelectField>
      <div className="terms">
        <div>
          <b>{formatPrice(hourlyRateCents, i18n.language)}</b>
          <span>{t('request.perHour')}</span>
        </div>
        {/* Decision 3 of SP-UX10.5: the rate is the plan's, the discount already in it. */}
        {discountPct > 0 && plan ? (
          <div>
            <b>−{discountPct} %</b>
            <span>{t('request.planDiscount', { plan: t(`admin.plans.${plan}`) })}</span>
          </div>
        ) : null}
        <div>
          <b>{t('request.quoteIn')}</b>
          <span>{t('request.quoteThen')}</span>
        </div>
      </div>
      <div className="field">
        <label htmlFor={messageId}>
          {t('request.message')} ({t('app.optional')})
        </label>
        <textarea
          id={messageId}
          value={message}
          maxLength={4000}
          rows={4}
          placeholder={t('request.messageHint')}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
      <div className="contact">
        <SelectField
          label={t('request.contact')}
          value={contact}
          onChange={(e) => setContact(e.target.value === 'phone' ? 'phone' : 'email')}
        >
          <option value="email">{t('request.contactBy.email')}</option>
          <option value="phone">{t('request.contactBy.phone')}</option>
        </SelectField>
        <TextField
          label={t('request.phone')}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+49 …"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={errors.phone}
        />
      </div>
      <TextField
        label={t('request.name')}
        optional
        autoComplete="name"
        maxLength={120}
        value={contactName}
        onChange={(e) => setName(e.target.value)}
      />
      <Checkbox
        label={t('request.consent')}
        checked={consent}
        onChange={(e) => setConsent(e.target.checked)}
        error={errors.consent}
      />
      <div className="acts">
        <Button type="submit" busy={send.isPending}>
          {t('request.submit')}
        </Button>
        <LinkButton to={backTo} variant="ghost">
          {t('request.cancel')}
        </LinkButton>
        <span className="hint">{t('request.note')}</span>
      </div>
    </form>
  );
}
